import React, { useRef, useCallback, useState } from 'react';
import {
  IonContent,
  IonButton,
  IonIcon,
  IonGrid,
  IonModal,
  IonRow,
  IonCol,
  IonList,
  IonItem,
  IonAvatar,
  IonLabel,
  IonNote,
} from '@ionic/react';
import { closeOutline, chevronForward, receiptOutline, alertCircle, calendarOutline } from 'ionicons/icons';
import { Activity } from '@rsmaids/shared/types/analytics';
import BasePageWithHeader, { TitleCard } from '../../components/BasePageWithHeader';
import { useScrollSnap } from '../../hooks/useScrollSnap';
import BookingDetails from '../bookingSystem/BookingDetails';
import Invoice from '../Invoice';
import styles from './ActivityGroupPage.module.scss';
import { localizeActivity, localizeActivityTitle } from '../../utils/activityI18n';

interface ActivityGroupPageProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  categoryLabel: string;
  activities: Activity[];
  getBubbleColor: (initial?: string) => string;
}

const ActivityGroupPage: React.FC<ActivityGroupPageProps> = ({
  isOpen,
  onClose,
  title,
  categoryLabel,
  activities,
  getBubbleColor,
}) => {
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);

  const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
  const contentRef = useRef<HTMLIonGridElement>(null);
  const titleCardRef = useRef<HTMLIonRowElement>(null);
  const rafTick = useRef(false);
  const wasAtTop = useRef(true);

  const applyProgress = useCallback((progress: number, smooth: boolean) => {
    const headerEl = document.querySelector<HTMLElement>('[data-header-title]');
    if (smooth) {
      if (headerEl) headerEl.style.transition = 'opacity 0.2s ease-out';
      if (titleCardRef.current) {
        titleCardRef.current.style.transition = 'opacity 0.2s ease-out, transform 0.2s ease-out';
      }
      setTimeout(() => {
        if (headerEl) headerEl.style.transition = '';
        if (titleCardRef.current) titleCardRef.current.style.transition = '';
      }, 220);
    }
    baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
    if (titleCardRef.current) {
      titleCardRef.current.style.setProperty('--content-opacity', String(1 - progress));
      titleCardRef.current.style.setProperty('--content-scale', `scale(${1 - progress * 0.2})`);
    }
  }, []);

  const handleScroll = useScrollSnap(contentRef, 66, applyProgress);

  const handleItemTap = (activity: Activity) => {
    if (activity.id.startsWith('zoho-')) {
      const invoiceId = activity.id.replace(/^zoho-(paid|overdue|invoice|draft|sent|viewed|void)-/, '');
      setSelectedInvoiceId(invoiceId);
    } else {
      const bookingId = activity.id.replace(/^(cancel|complete|overdue|confirm|booking)-/, '');
      setSelectedBookingId(bookingId);
    }
  };

  const getActivityTypeIcon = (type: string) => {
    const t = type.toLowerCase();
    if (t.includes('payment') || t.includes('invoice') || t.includes('cash')) return receiptOutline;
    if (t.includes('overdue') || t.includes('due')) return alertCircle;
    return calendarOutline;
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose} className={styles.activityGroupModal}>
      <div className={`${styles.modalWrapper} ${styles.page}`}>
        <BasePageWithHeader
          ref={baseHeaderRef}
          title={localizeActivityTitle(title)}
          toolbarClassName={styles.toolbar}
          smallTitleClassName={styles.smallTitle}
          titleHeaderClassName={styles.titleHeader}
          startButtons={
            <IonButton shape="round" className={styles.closeButton} onClick={onClose}>
              <IonIcon icon={closeOutline} />
            </IonButton>
          }
        >
          <IonContent scrollY={false} className={styles.page}>
            <IonGrid
              ref={contentRef}
              className={styles.listContainer}
              onScroll={(e) => {
                const el = e.target as HTMLElement;
                const top = el.scrollTop || 0;
                const atTop = top <= 5;
                if (atTop !== wasAtTop.current) wasAtTop.current = atTop;
                if (!rafTick.current) {
                  rafTick.current = true;
                  requestAnimationFrame(() => {
                    handleScroll(top);
                    rafTick.current = false;
                  });
                }
              }}
            >
              <IonRow ref={titleCardRef} className={styles.titleCardWrapper}>
                <IonCol>
                  <TitleCard
                    title={localizeActivityTitle(title)}
                    subtitle={categoryLabel}
                    subtitleClassName={styles.titleSub}
                    cardClassName={styles.titleCard}
                    titleContainerClassName={styles.titleContainer}
                    titleClassName={styles.titleText}
                  />
                </IonCol>
              </IonRow>

              <IonList lines="none" className={styles.list}>
                {activities.map((activity) => (
                  <IonItem
                    key={activity.id}
                    button
                    lines="none"
                    detail={false}
                    className={styles.activityGroupCard}
                    onClick={() => handleItemTap(activity)}
                  >
                    <IonAvatar slot="start" className={styles.activityIconWrapper}>
                      <IonAvatar className={styles.activityTypeCircle}>
                        <IonIcon icon={getActivityTypeIcon(activity.type)} className={styles.activityTypeIc} />
                      </IonAvatar>
                      <IonAvatar
                        className={styles.activityUserBadge}
                        style={{ background: getBubbleColor(activity.userInitial) }}
                      >
                        {activity.userInitial || '?'}
                      </IonAvatar>
                    </IonAvatar>

                    <IonLabel className={styles.activityContent}>
                      <span className={styles.activityTitle}>{localizeActivity(activity).title}</span>
                      <span className={styles.activityDetail}>{localizeActivity(activity).detail}</span>
                    </IonLabel>

                    <IonCol slot="end" size="auto" className={styles.activityRightCol}>
                      <IonNote className={styles.activityTimestamp}>{localizeActivity(activity).time}</IonNote>
                      <IonIcon icon={chevronForward} className={styles.activityChevron} />
                    </IonCol>
                  </IonItem>
                ))}
              </IonList>
            </IonGrid>
          </IonContent>
        </BasePageWithHeader>
      </div>

      <BookingDetails
        isOpen={!!selectedBookingId}
        onClose={() => setSelectedBookingId(null)}
        bookingId={selectedBookingId || undefined}
      />

      <IonModal isOpen={!!selectedInvoiceId} onDidDismiss={() => setSelectedInvoiceId(null)}>
        {selectedInvoiceId && (
          <Invoice
            invoiceId={selectedInvoiceId}
            onClose={() => setSelectedInvoiceId(null)}
          />
        )}
      </IonModal>
    </IonModal>
  );
};

export default ActivityGroupPage;
